import { ENTITY_TYPES } from '@sales-center/common'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { createSystemOpportunityName } from '@user/components/Opportunities/OpportunityName/functions'
import {
  IOpportunity,
  selectOpportunityById,
  selectOpportunityDetail,
} from '../opportunity'
import { StoreData } from '../reducers/typings'
import { ServiceConfig } from '../services/config'
import { APIError, get, post, put, remove } from '../services/crud'
import {
  ILeadOppProduct,
  ILeadOppProductDeleteModel,
  ILeadProductPostModel,
  LeadOppProductOrderInput,
} from './typings'
import { updateName as updateOpportunityName } from '../opportunity/api'
import { selectAllLeadOppProducts } from './slice'

const baseLeadUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.lead.product}`
const baseOppUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.opportunity.product}`

const getBaseUrl = (entityType: ENTITY_TYPES) =>
  entityType === ENTITY_TYPES.Lead ? baseLeadUrl : baseOppUrl

export const getLeadOppProducts = createAsyncThunk<
  ILeadOppProduct[],
  { entityId: number; entityType: ENTITY_TYPES },
  { rejectValue: APIError }
>('products', async ({ entityId, entityType }, thunkApi) => {
  const baseUrl = getBaseUrl(entityType)
  const path = entityType === ENTITY_TYPES.Lead ? 'lead' : 'opportunity'

  if (entityId === -1) return []

  const response = await get(`${baseUrl}/${path}/${entityId}`)

  if (response.ok) {
    const payload: any[] = await response.json()
    const ret: ILeadOppProduct[] = payload.map((p) => {
      const product = p as ILeadOppProduct
      return {
        ...product,
        entityId,
        initialDiscountType:
          p.initialDiscountType === 1 ? 'dollar' : 'percentage',
        recurringDiscountType:
          p.recurringDiscountType === 1 ? 'dollar' : 'percentage',
      }
    })
    return ret
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const createLeadOppProduct = createAsyncThunk<
  ILeadOppProduct,
  { product: ILeadOppProduct; entityType: ENTITY_TYPES; entityId: number },
  { rejectValue: APIError }
>('products/add', async ({ product, entityType }, thunkApi) => {
  const baseUrl = getBaseUrl(entityType)

  // exclude id from create
  const { id, entityId, ...rest } = product

  const leadOppProduct: ILeadProductPostModel = { ...rest }

  if (entityType === ENTITY_TYPES.Lead) {
    leadOppProduct.leadId = Number(entityId)
  } else {
    leadOppProduct.opportunityId = Number(entityId)
  }

  const response = await post(`${baseUrl}`, leadOppProduct)

  if (response.ok) {
    updateName(entityType, entityId, thunkApi, undefined, product, 'create')
    const payload: any = await response.json()
    const ret: ILeadOppProduct = {
      ...payload,
      entityId,
      initialDiscountType:
        payload.initialDiscountType === 1 ? 'dollar' : 'percentage',
      recurringDiscountType:
        payload.recurringDiscountType === 1 ? 'dollar' : 'percentage',
    }
    return ret
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const updateLeadOppProduct = createAsyncThunk<
  ILeadOppProduct,
  { product: ILeadOppProduct; entityType: ENTITY_TYPES },
  { rejectValue: APIError }
>('products/update', async ({ product, entityType }, thunkApi) => {
  const baseUrl = getBaseUrl(entityType)
  const response = await put(`${baseUrl}`, product)
  const { entityId } = product

  if (response.ok) {
    updateName(entityType, entityId, thunkApi, product.id, product, 'update')
    const payload: any = await response.json()
    const ret: ILeadOppProduct = {
      entityId: product.entityId,
      ...payload,
      initialDiscountType:
        payload.initialDiscountType === 1 ? 'dollar' : 'percentage',
      recurringDiscountType:
        payload.recurringDiscountType === 1 ? 'dollar' : 'percentage',
    }
    return ret
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const deleteLeadOppProduct = createAsyncThunk<
  ILeadOppProductDeleteModel,
  { id: number; entityType: ENTITY_TYPES; entityId: number | undefined },
  { rejectValue: APIError }
>('products/delete', async ({ id, entityType, entityId }, thunkApi) => {
  const baseUrl = getBaseUrl(entityType)
  const response = await remove(`${baseUrl}/${id}`)

  if (response.ok) {
    const responseModel: ILeadOppProductDeleteModel = await response.json()
    updateName(entityType, entityId, thunkApi, id, undefined, 'delete')
    return responseModel
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const reorderLeadOppProduct = createAsyncThunk<
  LeadOppProductOrderInput,
  LeadOppProductOrderInput,
  { rejectValue: APIError }
>('products/reorder', async (data, thunkApi) => {
  const baseUrl = getBaseUrl(data.entityType)
  const response = await put(
    `${baseUrl}${ServiceConfig.leadOppProduct.reorder}`,
    data,
  )
  if (response.ok) {
    return data
  } else {
    return thunkApi.rejectWithValue(await response.json())
  }
})

const updateName = (
  entityType: ENTITY_TYPES,
  entityId: number | undefined,
  thunkApi,
  id: number | undefined,
  product: ILeadOppProduct | undefined,
  updateType: 'create' | 'delete' | 'update',
) => {
  if (entityType === ENTITY_TYPES.Opportunity && entityId) {
    const state = thunkApi.getState() as StoreData
    const opportunity: IOpportunity | undefined = selectOpportunityById(
      state,
      entityId,
    )
    const opportunityDetail = selectOpportunityDetail(state)

    if (
      (opportunity && opportunity.useSystemName) ||
      (opportunityDetail && opportunityDetail.useSystemName)
    ) {
      const currentServices: ILeadOppProduct[] = selectAllLeadOppProducts(state)
      let newServices: ILeadOppProduct[]
      switch (updateType) {
        case 'create':
          newServices = currentServices
          if (product) newServices.push(product)
          break
        case 'delete':
          newServices = currentServices.filter((x) => x.id !== id)
          break
        case 'update':
          newServices = currentServices.map((obj) =>
            obj.id === id && product ? product : obj,
          )
          break
      }

      const newOppName: string = createSystemOpportunityName(newServices)
      const nameToCompare = opportunity
        ? opportunity.opportunityName
        : opportunityDetail?.opportunityName
      if (nameToCompare !== newOppName) {
        thunkApi.dispatch(
          updateOpportunityName({
            id: entityId,
            name: newOppName,
            useSystemName: true,
          }),
        )
      }
    }
  }
}
